import axios from "axios";
import {
  MEASUREMENT_LIST_REQUEST,
  MEASUREMENT_LIST_SUCCESS,
  MEASUREMENT_LIST_FAIL,
  MEASUREMENT_DELETE_REQUEST,
  MEASUREMENT_DELETE_SUCCESS,
  MEASUREMENT_DELETE_FAIL,
  MEASUREMENT_LIST_BY_STATUS_REQUEST,
  MEASUREMENT_LIST_BY_STATUS_SUCCESS,
  MEASUREMENT_LIST_BY_STATUS_FAIL,
} from "../../constants/MeasurementConstants";

export const fetchMeasurementData = async (
  measurementId,
  token,
  setTableData,
  existingData,
  hasUnsavedChanges,
  setTotalSqft,
  setTotalCost
) => {
  if (!measurementId) return;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurement/${measurementId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const fetchedData = response.data.measurements;
    setTotalSqft(response.data.totalSqft);
    setTotalCost(response.data?.totalCost);
    // Merge the data with existing user data, but only update the calculated fields
    setTableData((currentData) => {
      return currentData.map((existingRow, index) => {
        const fetchedRow = fetchedData[index] || {};

        // Only update fields that are calculated, not the user-entered fields
        return {
          ...existingRow, // Preserve user-entered values
          sqft: fetchedRow.sqft || existingRow.sqft, // Preserve existing sqft if not updated
          payment: fetchedRow.payment || existingRow.payment, // Preserve existing payment if not updated
          // Add more fields to update as needed
        };
      });
    });
  } catch (error) {
    console.error("Error fetching measurement data:", error);
  }
};

export const fetchMeasurementDataEdit = async (
  measurementId,
  token,
  setTableData,
  setTotalCost
) => {
  if (!measurementId) return;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurement/${measurementId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const fetchedData = response.data.measurements;
    const totalCost = response.data.totalCost;
    if (totalCost) {
      setTotalCost(totalCost);
    }

    if (fetchedData.length > 0) {
      setTableData(fetchedData);
    }
  } catch (error) {
    console.error("Error fetching measurement data:", error);
  }
};
export const createMeasurement = async (
  productType,
  tableData,
  dealerId,
  token,
  setMeasurementId,
  setTotalSqft,
  setIsMeasurementCreated,
  setLoading,
  loading
) => {
  if (loading) return;

  setLoading(true);
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/create-measurement`,
      {
        productType,
        tableData: tableData.filter(
          (row) =>
            row.width.trim() !== "" &&
            row.height.trim() !== "" &&
            row.liftStyle.trim() !== "" &&
            row.productMaterial.trim() !== "" &&
            row.productHeadrail.trim() !== ""
        ),
        dealerId,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setMeasurementId(response.data._id);
    setTotalSqft(response.data.totalSqft);
    setIsMeasurementCreated(true);
    setLoading(false);
  } catch (error) {
    console.error("Error creating measurement:", error);
    setLoading(false);
  }
};

export const updateMeasurement = async (
  measurementId,
  productType,
  tag,
  tableData,
  token,
  totalSqft,
  orderStatus,
) => {
  try {
    // Deep copy tableData to avoid mutation issues
    const filteredTableData = JSON.parse(JSON.stringify(tableData)).filter(
      (row) =>
        row.width?.trim() !== "" &&
        row.height?.trim() !== "" &&
        row.liftStyle?.trim() !== "" &&
        row.productMaterial?.trim() !== "" &&
        row.productHeadrail?.trim() !== ""
    );

    const dataToSend = {
      ...(productType && { productType }),
      ...(tag && { tag }),
      measurements: filteredTableData,
      ...(totalSqft && { totalSqft }),
      ...(orderStatus && { orderStatus }),
    };

    // console.log("Payload to send:", JSON.stringify(dataToSend, null, 2));

    const response = await axios.put(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurement/${measurementId}`,
      dataToSend,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    // console.log("Update response:", response.data);
    return response.data; 
  } catch (error) {
    console.error(
      "Error updating measurement:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};
//measurement route=/order/measurements

// Fetch Measurements
export const listMeasurements =
  (tag = "", page = 1, limit, statuses = ["measured"]) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: MEASUREMENT_LIST_REQUEST });

      const {
        dealerLogin: { dealerInfo },
      } = getState();

      const { data } = await axios.get(
        `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurements/dealer`,
        {
          params: { tag, page, limit, statuses: statuses.join(",") },
          headers: {
            Authorization: `Bearer ${dealerInfo.token}`,
          },
        }
      );
      dispatch({
        type: MEASUREMENT_LIST_SUCCESS,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: MEASUREMENT_LIST_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

// Delete Measurement
export const deleteMeasurement = (id) => async (dispatch, getState) => {
  try {
    dispatch({ type: MEASUREMENT_DELETE_REQUEST });

    const {
      dealerLogin: { dealerInfo },
    } = getState();

    await axios.delete(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurements/${id}`,
      {
        headers: {
          Authorization: `Bearer ${dealerInfo.token}`,
        },
      }
    );

    dispatch({ type: MEASUREMENT_DELETE_SUCCESS, payload: id });
  } catch (error) {
    dispatch({
      type: MEASUREMENT_DELETE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

//fetch quotation by filtering the document with orderstatus
export const listMeasurementsByStatus =
  (tag = "", page = 1, limit, statuses = ["quoted"]) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: MEASUREMENT_LIST_BY_STATUS_REQUEST });

      const {
        dealerLogin: { dealerInfo },
      } = getState();

      const { data } = await axios.get(
        `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurements/dealer`,
        {
          params: { tag, page, limit, statuses: statuses.join(",") },
          headers: {
            Authorization: `Bearer ${dealerInfo.token}`,
          },
        }
      );
      console.log(data);
      dispatch({
        type: MEASUREMENT_LIST_BY_STATUS_SUCCESS,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: MEASUREMENT_LIST_BY_STATUS_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

//Search suggestions
export const fetchSearchSuggestions = () => async (dispatch, getState) => {
  try {
    const {
      dealerLogin: { dealerInfo },
    } = getState();

    const { data } = await axios.get(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurements/dealer/suggestions`,
      {
        headers: {
          Authorization: `Bearer ${dealerInfo.token}`,
        },
      }
    );
    dispatch({ type: "FETCH_SUGGESTIONS_SUCCESS", payload: data });
  } catch (error) {
    dispatch({ type: "FETCH_SUGGESTIONS_FAIL", payload: error.message });
  }
};

export const fetchQuotationSuggestion = () => async (dispatch, getState) => {
  try {
    const {
      dealerLogin: { dealerInfo },
    } = getState();

    const { data } = await axios.get(
      `${process.env.REACT_APP_DP_API}/api/dealer-portal/measurements/dealer/suggestions`,
      {
        params: {
          search: "",
          status: ["confirmed", "quoted"].join(","),
        },
        headers: {
          Authorization: `Bearer ${dealerInfo.token}`,
        },
      }
    );

    dispatch({ type: "FETCH_QUOTATIONS_SUGGESTIONS_SUCCESS", payload: data });
  } catch (error) {
    dispatch({
      type: "FETCH_QUOTATIONS_SUGGESTIONS_FAIL",
      payload: error.message,
    });
  }
};
